/**
 * Утилита для хранения иконок кастомных категорий
 * Использует localStorage для сохранения маппинга категория -> иконка
 */

const STORAGE_KEY = 'custom_category_icons';

// Простой EventEmitter для уведомления об изменении иконок
class SimpleEventEmitter {
  private listeners: Map<string, Set<Function>> = new Map();

  on(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: Function) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  emit(event: string, ...args: any[]) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  removeAllListeners(event?: string) {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

export const categoryIconsEventEmitter = new SimpleEventEmitter();
export const CATEGORY_ICONS_CHANGED_EVENT = 'categoryIconsChanged';

interface CategoryIconMap {
  [categoryName: string]: string; // categoryName -> iconName
}

/**
 * Получает все сохраненные иконки из localStorage
 */
function getCategoryIcons(): CategoryIconMap {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    }
    return {};
  } catch (error) {
    console.error('❌ Error reading category icons:', error);
    return {};
  }
}

/**
 * Сохраняет иконку для категории
 */
export async function saveCategoryIcon(categoryName: string, iconName: string): Promise<void> {
  try {
    const icons = getCategoryIcons();
    icons[categoryName] = iconName;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(icons));
    // Уведомляем об изменении иконок
    categoryIconsEventEmitter.emit(CATEGORY_ICONS_CHANGED_EVENT, icons);
  } catch (error) {
    console.error('❌ Error saving category icon:', error);
  }
}

/**
 * Получает иконку для категории
 */
export async function getCategoryIcon(categoryName: string): Promise<string | null> {
  try {
    const icons = getCategoryIcons();
    return icons[categoryName] || null;
  } catch (error) {
    console.error('❌ Error getting category icon:', error);
    return null;
  }
}

/**
 * Удаляет иконку для категории
 */
export async function deleteCategoryIcon(categoryName: string): Promise<void> {
  try {
    const icons = getCategoryIcons();
    delete icons[categoryName];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(icons));
    // Уведомляем об изменении иконок
    categoryIconsEventEmitter.emit(CATEGORY_ICONS_CHANGED_EVENT, icons);
  } catch (error) {
    console.error('❌ Error deleting category icon:', error);
  }
}

/**
 * Получает все сохраненные иконки (синхронная версия для использования в компонентах)
 */
export function getAllCategoryIcons(): CategoryIconMap {
  return getCategoryIcons();
}

